"use client";

import Link from "next/link";
import { useState, type KeyboardEvent } from "react";
import { useTranslations } from "next-intl";
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Fuel,
  Luggage,
  Settings2,
  Users,
  X,
} from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useCurrency } from "@/contexts/CurrencyContext";
import {
  carClassOf,
  carColorsOf,
  carSpecsOf,
  type CarSpecs,
} from "@/lib/mobility/car-rental";
import { periodTotal } from "@/lib/mobility/pricing";
import { cn } from "@/lib/utils";
import type { Product } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { CarOfferPanel } from "./CarOfferPanel";

type CarT = ReturnType<typeof useTranslations<"search.car">>;

interface CarRentalListProps {
  products: Product[];
  size: "mobile" | "tablet" | "desktop";
  locale: string;
  /** 상품 링크 꼬리 — "?checkin=…&checkout=…" (없으면 "") */
  dateQuery: string;
  /** 검색 날짜의 대여일수(결제와 같은 계산) — 모바일 시트 총 요금. 날짜가 없으면 null */
  rentalDays?: number | null;
  onProductHover?: (id: string | null) => void;
  unavailableMap?: Map<string, boolean>;
}

/**
 * 렌터카 검색 목록 — 10-13 미리보기(v13) 확정안.
 * 데스크톱·태블릿: 테두리·구분선 없는 한 줄 행, 행 사이는 간격만(사진 · 차급/이름/스펙/배지 ·
 * 업체 로고·이름 / 1일가 + 펼치기), 행을 누르면 회색 면의 조건이 열린다.
 * 모바일: 사진 왼쪽·글자 오른쪽(구분선 없음), 누르면 바텀 시트(요약 → 차량 설명 → 조건).
 * 목록 가격은 1일가만(10-13). 모바일 시트 하단만 1일가 + 검색 날짜의 총 요금(10-14).
 */
export function CarRentalList({
  products,
  size,
  locale,
  dateQuery,
  rentalDays = null,
  onProductHover,
  unavailableMap,
}: CarRentalListProps) {
  const t = useTranslations("search.car");
  const tSearch = useTranslations("search");
  // 금액은 기본 상품 카드(PriceDisplay showCurrencyCode)와 같은 통화 코드 표기 — "JPY 14,400"
  const { formatWithCodeFromSource } = useCurrency();
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [sheetId, setSheetId] = useState<string | null>(null);

  const dailyOf = (product: Product) => {
    const daily = product.price_per_day;
    return daily && daily > 0
      ? formatWithCodeFromSource(
          daily,
          (product.currency || "USD") as SupportedCurrency,
        )
      : null;
  };
  const totalOf = (product: Product) => {
    if (!rentalDays) return null;
    const total = periodTotal(product, rentalDays);
    return total && total > 0
      ? t("sheetTotal", {
          price: formatWithCodeFromSource(
            total,
            (product.currency || "USD") as SupportedCurrency,
          ),
          days: rentalDays,
        })
      : null;
  };
  const hrefOf = (product: Product) =>
    `/${locale}/products/${product.id}${dateQuery}`;
  const soldOutLabel = tSearch("availability.soldOut");

  if (size === "mobile") {
    const sheetProduct =
      products.find((product) => product.id === sheetId) ?? null;
    return (
      <>
        <div className="flex flex-col gap-1 px-3">
          {products.map((product) => (
            <button
              key={product.id}
              type="button"
              onClick={() => setSheetId(product.id)}
              className="block w-full py-3.5 text-left"
            >
              {/* 사진(3:2, 168×112) 왼쪽 · 글자 오른쪽, 1일가는 오른쪽 열 맨 아래(10-13).
                  카드 높이는 사진 높이 112px 로 통일 — 스펙이 두 줄로 넘치는 차만 조금 길어진다. */}
              <div className="grid grid-cols-[168px_minmax(0,1fr)] gap-3">
                <CarThumb product={product} className="h-28 w-42 rounded-[10px]" withColors />
                <div className="flex min-h-28 min-w-0 flex-col">
                  <CarSummary
                    product={product}
                    specs={carSpecsOf(product.specifications)}
                    t={t}
                    compact
                    topLine="partner"
                  />
                  <div className="mt-auto flex justify-end pt-1 tabular-nums">
                    <DailyPrice
                      label={dailyOf(product)}
                      soldOut={unavailableMap?.get(product.id)}
                      soldOutLabel={soldOutLabel}
                      t={t}
                    />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <Drawer
          open={sheetProduct !== null}
          onOpenChange={(next) => !next && setSheetId(null)}
        >
          {/* 내용 높이만큼(fit) — 기본 DrawerContent 는 h-full 이라 최대치까지 늘어난다. 넘치면 본문만 스크롤 */}
          <DrawerContent className="h-auto max-h-[82dvh] bg-white p-0">
            {sheetProduct ? (
              <>
                <VisuallyHidden>
                  <DrawerTitle>{sheetProduct.name}</DrawerTitle>
                </VisuallyHidden>
                <div className="shrink-0 px-4 pb-3">
                  {/* 닫기 = 오른쪽 회색 원(10-14) */}
                  <div className="flex justify-end">
                    <DrawerClose
                      aria-label={t("close")}
                      className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-gray-700 transition hover:bg-gray-200"
                    >
                      <X className="h-4.5 w-4.5" />
                    </DrawerClose>
                  </div>
                  <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)_104px] items-center gap-2.5">
                    <CarSummary
                      product={sheetProduct}
                      specs={carSpecsOf(sheetProduct.specifications)}
                      t={t}
                      compact
                      withYear
                      topLine="partner"
                    />
                    <CarThumb
                      product={sheetProduct}
                      className="h-17.5 w-26 rounded-[10px]"
                    />
                  </div>
                </div>
                {/* 아래 여백 = ResponsiveModal 시트 본문과 같은 규칙 max(안전 영역, 16px) */}
                <div className="min-h-0 flex-initial overflow-y-auto border-t border-gray-100 bg-gray-50 px-3 pb-[max(var(--loc-safe-area-inset-bottom),16px)]">
                  <Link
                    href={hrefOf(sheetProduct)}
                    className="mb-3 flex items-center gap-2 border-b border-gray-200 px-1 py-3 text-[13px] text-gray-700"
                  >
                    {t("carDescription")}
                    <ChevronDown className="ml-auto h-4 w-4 -rotate-90 text-gray-400" />
                  </Link>
                  <CarOfferPanel
                    productId={sheetProduct.id}
                    locale={locale}
                    layout="mobile"
                    priceLabel={dailyOf(sheetProduct)}
                    totalLabel={totalOf(sheetProduct)}
                    href={hrefOf(sheetProduct)}
                  />
                </div>
              </>
            ) : null}
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-2">
      {products.map((product) => {
        const isOpen = open.has(product.id);
        const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle(product.id);
          }
        };
        return (
          <article
            key={product.id}
            onMouseEnter={() => onProductHover?.(product.id)}
            onMouseLeave={() => onProductHover?.(null)}
          >
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-label={t("expand", { name: product.name })}
              onClick={() => toggle(product.id)}
              onKeyDown={onKeyDown}
              className={cn(
                "grid cursor-pointer gap-4 rounded-xl py-4 outline-none focus-visible:ring-2 focus-visible:ring-gray-900",
                // 오른쪽 칸은 금액 폭만큼(auto) — 고정 폭이면 "KRW 143,146/일"이 칸을 넘어
                // 왼쪽 구분선 위로 그려졌다(10-15 태블릿). 최소 폭은 아래 칸이 잡는다.
                size === "tablet"
                  ? "grid-cols-[140px_minmax(0,1fr)_auto]"
                  : "grid-cols-[176px_minmax(0,1fr)_auto]",
              )}
            >
              <CarThumb
                product={product}
                className={cn(
                  "rounded-xl",
                  size === "tablet" ? "h-25 w-35" : "h-31 w-44",
                )}
                withColors
              />
              <div className="pt-0.5">
                <CarSummary
                  product={product}
                  specs={carSpecsOf(product.specifications)}
                  t={t}
                  withYear
                />
              </div>
              <div
                className={cn(
                  "flex flex-col items-end border-l border-gray-100 pl-4 text-right",
                  size === "tablet" ? "min-w-37.5" : "min-w-42",
                )}
              >
                {product.partner_name ? (
                  // 업체명이 길어도 칸을 밀어 넓히지 않게 — 넘치면 말줄임
                  <div className="flex max-w-44 min-w-0 justify-end">
                    <PartnerLine
                      name={product.partner_name}
                      logo={product.partner_logo_url}
                    />
                  </div>
                ) : null}
                {/* 가격과 펼치기 버튼은 아래쪽에 한 묶음(10-13) */}
                <div className="mb-2 mt-auto tabular-nums">
                  <DailyPrice
                    label={dailyOf(product)}
                    soldOut={unavailableMap?.get(product.id)}
                    soldOutLabel={soldOutLabel}
                    t={t}
                    large
                  />
                </div>
                <span
                  aria-hidden
                  className="grid h-9 w-9 place-items-center rounded-[10px] border border-orange-300 bg-orange-50 text-orange-600"
                >
                  <ChevronDown
                    className={cn(
                      "h-4.5 w-4.5 transition-transform duration-200",
                      isOpen && "rotate-180",
                    )}
                  />
                </span>
              </div>
            </div>
            {isOpen ? (
              <div className="mb-4 rounded-[14px] bg-gray-50 px-4 py-3.5">
                <CarOfferPanel
                  productId={product.id}
                  locale={locale}
                  layout="desktop"
                  priceLabel={dailyOf(product)}
                  href={hrefOf(product)}
                />
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

/** 업체명 앞 20px 원형 로고 — 로고가 없으면 회색 원 + 이름 첫 글자 */
function PartnerLine({
  name,
  logo,
  chevron = false,
}: {
  name: string;
  logo?: string | null;
  /** 이름 뒤 › — 모바일 카드 윗줄(기본 상품 카드의 "업체 ›"와 같은 모양) */
  chevron?: boolean;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 text-xs text-gray-500">
      <span
        aria-hidden
        className="relative grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-full border border-gray-200 bg-gray-100 text-[10px] font-semibold text-gray-600"
      >
        {logo ? (
          <OptimizedImage
            src={logo}
            alt=""
            fill
            sizes="20px"
            enablePlaceholder={false}
            className="object-cover"
          />
        ) : (
          Array.from(name.trim())[0]
        )}
      </span>
      <span className="truncate">{name}</span>
      {chevron ? (
        <ChevronRight aria-hidden className="-ml-1 h-3.5 w-3.5 shrink-0 text-gray-400" />
      ) : null}
    </span>
  );
}

function CarThumb({
  product,
  className,
  withColors = false,
}: {
  product: Product;
  className: string;
  /** 사진 오른쪽 아래에 색상 동그라미(겹침) — 색을 못 읽으면 그리지 않는다(10-13) */
  withColors?: boolean;
}) {
  const src = product.images?.[0];
  // 색이 하나뿐이면 고를 것이 없어 그리지 않는다 — 2색 이상만(10-14)
  const colors = withColors ? carColorsOf(product.specifications) : [];
  const showColors = colors.length >= 2;
  return (
    <div
      className={cn("relative shrink-0 overflow-hidden bg-white", className)}
    >
      {src ? (
        <OptimizedImage
          src={src}
          alt={product.name}
          fill
          sizes="176px"
          enablePlaceholder={false}
          className="object-cover"
        />
      ) : null}
      {showColors ? (
        <span
          aria-hidden
          className="absolute bottom-1.5 right-1.5 flex rounded-full bg-white/90 p-0.5 shadow-[0_1px_3px_rgba(0,0,0,0.15)]"
        >
          {colors.map((pair, index) => (
            <span
              key={index}
              className={cn(
                "h-3.5 w-3.5 rounded-full border-[1.5px] border-white shadow-[inset_0_0_0_1px_rgba(17,24,39,0.18)]",
                index > 0 && "-ml-1.25",
              )}
              style={{
                background:
                  pair.length > 1
                    ? `linear-gradient(135deg, ${pair[0]} 50%, ${pair[1]} 50%)`
                    : pair[0],
              }}
            />
          ))}
        </span>
      ) : null}
    </div>
  );
}

/**
 * 윗줄(차급 또는 파트너명) · 이름 · 스펙 줄 — 행·카드·시트 공용.
 * 색상 수 배지는 사진 위 색 동그라미가 대신한다(10-13).
 */
function CarSummary({
  product,
  specs,
  t,
  compact = false,
  withYear = false,
  topLine = "class",
}: {
  product: Product;
  specs: CarSpecs;
  t: CarT;
  compact?: boolean;
  withYear?: boolean;
  /** 이름 위 한 줄 — 차급(데스크톱 행) 또는 파트너명(모바일 카드·시트, 기본 카드와 같은 13px · 600) */
  topLine?: "class" | "partner";
}) {
  const carClass = carClassOf(product, specs);
  const iconClass = compact ? "h-[13px] w-[13px]" : "h-[15px] w-[15px]";
  const items: Array<{ key: string; Icon: typeof Users; text: string }> = [];
  if (specs.seats !== null)
    items.push({
      key: "seats",
      Icon: Users,
      text: t("seats", { n: specs.seats }),
    });
  if (specs.bags) {
    const key =
      specs.bags.kind === "exact"
        ? "bagsExact"
        : specs.bags.kind === "approx"
        ? "bagsApprox"
        : "bagsMin";
    items.push({
      key: "bags",
      Icon: Luggage,
      text: t(key, { n: specs.bags.n }),
    });
  }
  if (specs.fuel)
    items.push({ key: "fuel", Icon: Fuel, text: t(`fuel.${specs.fuel}`) });
  if (specs.drive)
    items.push({
      key: "drive",
      Icon: Settings2,
      text: specs.drive.toUpperCase(),
    });
  if (withYear && specs.year) {
    items.push({
      key: "year",
      Icon: CalendarDays,
      text:
        specs.year.min === specs.year.max
          ? t("year", { year: specs.year.min })
          : t("yearRange", { min: specs.year.min, max: specs.year.max }),
    });
  }

  return (
    <div className={cn("flex min-w-0 flex-col", compact ? "gap-1" : "gap-1.5")}>
      {topLine === "partner" && product.partner_name ? (
        <PartnerLine
          name={product.partner_name}
          logo={product.partner_logo_url}
          chevron
        />
      ) : (
        <span className="text-xs text-gray-500">{t(`class.${carClass}`)}</span>
      )}
      <span
        className={cn(
          "font-semibold text-gray-900",
          compact ? "text-[15px]" : "text-base",
        )}
      >
        {product.name}
      </span>
      {items.length > 0 ? (
        <div
          className={cn(
            "flex flex-wrap text-gray-700",
            compact
              ? "gap-x-2.5 gap-y-0.5 text-xs"
              : "gap-x-3.5 gap-y-1 text-[13px]",
          )}
        >
          {items.map(({ key, Icon, text }) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 whitespace-nowrap"
            >
              <Icon
                aria-hidden
                className={cn("shrink-0 text-gray-500", iconClass)}
                strokeWidth={1.8}
              />
              {text}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DailyPrice({
  label,
  soldOut,
  soldOutLabel,
  t,
  large = false,
}: {
  label: string | null;
  soldOut?: boolean;
  soldOutLabel: string;
  t: CarT;
  large?: boolean;
}) {
  if (soldOut) {
    return (
      <span className="text-[15px] font-semibold text-gray-600">
        {soldOutLabel}
      </span>
    );
  }
  if (!label) return null;
  return (
    <div
      suppressHydrationWarning
      className={cn(
        "whitespace-nowrap font-bold text-gray-900",
        large ? "text-xl" : "text-base",
      )}
    >
      {label}
      <span className="ml-0.5 text-xs font-medium text-gray-500">
        {t("perDay")}
      </span>
    </div>
  );
}
